import { useState } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import BridgeSection from './components/BridgeSection';
import SwapSection from './components/SwapSection';
import PoolsOverview from './components/PoolsOverview';
import LiquiditySection from './components/LiquiditySection';
import FarmsOverview from './components/FarmsOverview';
import FarmSection from './components/FarmSection';
import Dashboard from './components/Dashboard';
import TokenRegistration from './components/TokenRegistration';
import Leaderboard from './components/Leaderboard';
import Guide from './components/Guide';
import Contracts from './components/Contracts';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import StatsBar from './components/StatsBar';

function App() {
  const [activeTab, setActiveTab] = useState('bridge');
  const [selectedPool, setSelectedPool] = useState(null);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [poolsDropdownOpen, setPoolsDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const { isConnected } = useAccount();

  const handleNavigateToLiquidity = (pool) => {
    setSelectedPool(pool);
    setActiveTab('liquidity');
  };

  const handleNavigateToFarm = (farm) => {
    setSelectedFarm(farm);
    setActiveTab('farm-detail');
  };

  return (
    <div className="App">
      {/* Top navbar */}
      <div className="topbar">
        <div className="topbar-logo">
          <img
            src="https://copper-causal-dove-26.mypinata.cloud/ipfs/bafybeiardzfjvppccwjmmnbea3tna4exjkl5yqiuejank43hqvzvimc4ou"
            alt="EmberX"
            style={{ height: '28px', width: '28px', objectFit: 'contain' }}
          />
          <span>Ember<span style={{ color: '#FB923C' }}>X</span></span>
        </div>
        <div className="topbar-connect">
          <ConnectButton chainStatus="none" showBalance={false} />
        </div>
      </div>

      <div className="sub">Bridge • Swap • Pool • Earn</div>

      {/* Platform Stats */}
      <StatsBar />

      {/* Navigation */}
      <div className="nav-tabs">
        {/* Bridge */}
        <div
          className={`nav-tab ${activeTab === 'bridge' ? 'active' : ''}`}
          onClick={() => setActiveTab('bridge')}
        >
          Bridge
        </div>

        {/* Swap */}
        <div
          className={`nav-tab ${activeTab === 'swap' ? 'active' : ''}`}
          onClick={() => setActiveTab('swap')}
        >
          Swap
        </div>

        {/* Pools Dropdown */}
        <div className="nav-dropdown">
          <div
            className={`nav-tab ${['pools', 'liquidity'].includes(activeTab) ? 'active' : ''}`}
            onClick={() => setPoolsDropdownOpen(!poolsDropdownOpen)}
          >
            Pools ▾
          </div>
          {poolsDropdownOpen && (
            <div className="nav-dropdown-content">
              <div
                className={`nav-dropdown-item ${activeTab === 'liquidity' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('liquidity');
                  setSelectedPool(null);
                  setPoolsDropdownOpen(false);
                }}
              >
                Liquidity
              </div>
              <div
                className={`nav-dropdown-item ${activeTab === 'pools' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('pools');
                  setPoolsDropdownOpen(false);
                }}
              >
                Live Pools
              </div>
            </div>
          )}
        </div>

        {/* Earn (formerly Farms) */}
        <div
          className={`nav-tab ${activeTab === 'farms' ? 'active' : ''}`}
          onClick={() => setActiveTab('farms')}
        >
          Earn
        </div>

        {/* More Menu ••• */}
        <div className="nav-dropdown">
          <div
            className={`nav-tab ${['dashboard', 'leaderboard', 'register', 'guide', 'contracts'].includes(activeTab) ? 'active' : ''}`}
            onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
          >
            •••
          </div>
          {moreDropdownOpen && (
            <div className="nav-dropdown-content">
              <div
                className={`nav-dropdown-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('dashboard');
                  setMoreDropdownOpen(false);
                }}
              >
                Dashboard
              </div>
              <div
                className={`nav-dropdown-item ${activeTab === 'leaderboard' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('leaderboard');
                  setMoreDropdownOpen(false);
                }}
              >
                Leaderboard
              </div>
              <div
                className={`nav-dropdown-item ${activeTab === 'register' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('register');
                  setMoreDropdownOpen(false);
                }}
              >
                List Token
              </div>
              <div
                className={`nav-dropdown-item ${activeTab === 'guide' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('guide');
                  setMoreDropdownOpen(false);
                }}
              >
                Guide
              </div>
              <div
                className={`nav-dropdown-item ${activeTab === 'contracts' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('contracts');
                  setMoreDropdownOpen(false);
                }}
              >
                Contracts
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content Sections */}
      {activeTab === 'bridge' && <BridgeSection isConnected={isConnected} />}
      {activeTab === 'swap' && <SwapSection isConnected={isConnected} />}
      {activeTab === 'pools' && (
        <PoolsOverview
          isConnected={isConnected}
          onNavigateToLiquidity={handleNavigateToLiquidity}
        />
      )}
      {activeTab === 'liquidity' && (
        <LiquiditySection
          isConnected={isConnected}
          selectedPool={selectedPool}
        />
      )}
      {activeTab === 'farms' && (
        <FarmsOverview
          isConnected={isConnected}
          onNavigateToFarm={handleNavigateToFarm}
        />
      )}
      {activeTab === 'farm-detail' && (
        <FarmSection
          isConnected={isConnected}
          selectedFarm={selectedFarm}
        />
      )}
      {activeTab === 'dashboard' && <Dashboard onNavigate={setActiveTab} />}
      {activeTab === 'register' && <TokenRegistration />}
      {activeTab === 'leaderboard' && <Leaderboard onNavigate={setActiveTab} />}
      {activeTab === 'guide' && <Guide onNavigate={setActiveTab} />}
      {activeTab === 'contracts' && <Contracts />}
      {activeTab === 'terms' && <TermsAndConditions onNavigate={setActiveTab} />}
      {activeTab === 'privacy' && <PrivacyPolicy onNavigate={setActiveTab} />}
    </div>
  );
}

export default App;
